'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewStateValues } from '@/app/(home)/inbox/types';
import type { PrV2RequestedTeam } from '@/lib/pr-v2/metadata-summary';
import type { PrV2ReviewResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { ReviewerSelection, TeamReviewerSelection } from '@/types/github';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dismissPrV2Review, updatePrV2Reviewers } from './fetch-pr-v2';
import { prV2ReviewerMutationKey } from './mutation-keys';
import { prV2CommentsQueryKey, prV2DiscussionQueryKey, prV2ReviewsQueryKey, prV2SummaryQueryKey } from './query-keys';
import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import { inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { ReviewerState } from '@/types/github';
type ReviewerBatchInput = {
  toAdd: ReviewerSelection[];
  toRemove: ReviewerSelection[];
  teamsToAdd: TeamReviewerSelection[];
  teamsToRemove: TeamReviewerSelection[];
};
type DismissReviewInput = {
  reviewId: number;
  message: string;
};
type BatchMutationContext = {
  inverseInput: ReviewerBatchInput;
};
type ReviewMutationContext = {
  previousReview?: PrV2ReviewResponse;
};
type ReRequestMutationContext = {
  optimisticReviewId: string;
  inverseInput: ReviewerBatchInput;
};

// Delay before marking reviewer-related queries as stale after a successful mutation.
// This is a soft invalidation (refetchType: 'none') so the optimistic UI persists until
// a natural trigger (realtime event, window focus) refetches with server-confirmed data.
const REVIEWER_INVALIDATION_DELAY_MS = 1500;
const fallbackInvalidationTimeoutByPr = new Map<string, ReturnType<typeof setTimeout>>();
function normalizeLogin(login: string): string {
  return login.toLowerCase();
}

// Mirrors the server-side deriveReviewState in the inbox route. Scans reviews in order
// (assumed newest-first) and returns the first actionable state.
function deriveReviewStateFromReviews(reviews: Array<{
  state: string;
}>): ReviewStateValues {
  for (const review of reviews) {
    const s = review.state?.toUpperCase();
    if (s === 'COMMENTED' || s === 'DISMISSED') {
      continue;
    }
    if (s === 'CHANGES_REQUESTED' || s === 'APPROVED' || s === 'PENDING') {
      return s as ReviewStateValues;
    }
  }
  return 'NEEDS_REVIEW';
}
function getReviewerLogins(reviewers: ReviewerSelection[]): string[] {
  return reviewers.map(reviewer => reviewer.login);
}
function getReviewerAvatarUrl(login: string, requestedReviewers: Array<{
  login: string;
  avatar_url: string | null;
}>, reviews: PrV2ReviewResponse[]): string | null {
  const normalizedLogin = normalizeLogin(login);
  const requestedReviewer = requestedReviewers.find(reviewer => normalizeLogin(reviewer.login) === normalizedLogin);
  if (requestedReviewer) {
    return requestedReviewer.avatar_url;
  }
  const matchingReview = [...reviews].reverse().find(review => normalizeLogin(review.userLogin ?? '') === normalizedLogin);
  return matchingReview?.userAvatarUrl ?? null;
}
function getRequestedReviewersFromSummary(current: PrV2SummaryResponse | undefined): Array<{
  login: string;
  avatar_url: string | null;
}> {
  return current?.pullRequest.metadataSummary?.reviewers.map(reviewer => ({
    login: reviewer.login,
    avatar_url: reviewer.avatarUrl
  })) ?? [];
}

// Teams are keyed by slug (what the GitHub API takes); legacy summaries may
// only carry a name, so fall back to it for matching.
function getTeamKey(team: {
  slug: string | null;
  name: string;
}): string {
  return normalizeLogin(team.slug ?? team.name);
}
function getRequestedTeamsFromSummary(current: PrV2SummaryResponse | undefined): PrV2RequestedTeam[] {
  return current?.pullRequest.metadataSummary?.requestedTeams ?? [];
}
function getUpdatedRequestedTeams(requestedTeams: PrV2RequestedTeam[], input: ReviewerBatchInput): PrV2RequestedTeam[] {
  const removeKeys = new Set(input.teamsToRemove.map(getTeamKey));
  const nextRequestedTeams = requestedTeams.filter(team => !removeKeys.has(getTeamKey(team)));
  const nextTeamKeys = new Set(nextRequestedTeams.map(getTeamKey));
  input.teamsToAdd.forEach(team => {
    if (nextTeamKeys.has(getTeamKey(team))) {
      return;
    }
    nextRequestedTeams.push({
      name: team.name,
      slug: team.slug
    });
    nextTeamKeys.add(getTeamKey(team));
  });
  return nextRequestedTeams;
}
function withUpdatedRequestedReviewers(current: PrV2SummaryResponse | undefined, reviews: PrV2ReviewResponse[], input: ReviewerBatchInput): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }
  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};
  const requestedReviewers = getRequestedReviewersFromSummary(current);
  const removeLogins = new Set(getReviewerLogins(input.toRemove).map(normalizeLogin));
  const nextRequestedReviewers = requestedReviewers.filter(reviewer => !removeLogins.has(normalizeLogin(reviewer.login)));
  const nextRequestedLogins = new Set(nextRequestedReviewers.map(reviewer => normalizeLogin(reviewer.login)));
  input.toAdd.forEach(reviewer => {
    const login = reviewer.login;
    const normalizedLogin = normalizeLogin(login);
    if (nextRequestedLogins.has(normalizedLogin)) {
      return;
    }
    nextRequestedReviewers.push({
      login,
      avatar_url: reviewer.avatar_url || getReviewerAvatarUrl(login, requestedReviewers, reviews)
    });
    nextRequestedLogins.add(normalizedLogin);
  });
  const nextRequestedTeams = getUpdatedRequestedTeams(getRequestedTeamsFromSummary(current), input);
  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        reviewers: nextRequestedReviewers.map(reviewer => ({
          login: reviewer.login,
          avatarUrl: reviewer.avatar_url
        })),
        requestedTeams: nextRequestedTeams
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          requested_reviewers: nextRequestedReviewers,
          requested_teams: nextRequestedTeams
        }
      }
    }
  };
}
function getRequestedReviewers(current: PrV2SummaryResponse | undefined): Array<{
  login: string;
  avatar_url: string | null;
}> {
  return getRequestedReviewersFromSummary(current);
}
function withReRequestedReview(current: PrV2ReviewResponse[] | undefined, reviewerLogin: string, optimisticReviewId: string): PrV2ReviewResponse[] | undefined {
  if (!current) {
    return current;
  }
  const latestMatchingReview = [...current].reverse().find(review => normalizeLogin(review.userLogin ?? '') === normalizeLogin(reviewerLogin));
  return [...current, {
    id: optimisticReviewId,
    githubReviewId: optimisticReviewId,
    githubReviewNodeId: null,
    state: ReviewerState.REQUESTED_REVIEW,
    body: null,
    submittedAt: new Date().toISOString(),
    userLogin: reviewerLogin,
    userAvatarUrl: latestMatchingReview?.userAvatarUrl ?? null,
    commitId: null,
    raw: null
  }];
}
function removeReviewById(current: PrV2ReviewResponse[] | undefined, reviewId: string): PrV2ReviewResponse[] | undefined {
  if (!current) {
    return current;
  }
  return current.filter(review => review.githubReviewId !== reviewId);
}
function withDismissedReview(current: PrV2ReviewResponse[] | undefined, reviewId: number): PrV2ReviewResponse[] | undefined {
  if (!current) {
    return current;
  }
  const reviewIdString = String(reviewId);
  return current.map(review => review.githubReviewId === reviewIdString ? {
    ...review,
    state: ReviewerState.DISMISSED
  } : review);
}
function restoreReview(current: PrV2ReviewResponse[] | undefined, previousReview: PrV2ReviewResponse | undefined): PrV2ReviewResponse[] | undefined {
  if (!current || !previousReview) {
    return current;
  }
  return current.map(review => review.githubReviewId === previousReview.githubReviewId ? previousReview : review);
}
function scheduleReviewerFallbackInvalidation(prScopeKey: string, softInvalidateAfterMutation: () => Promise<void>) {
  const existingTimeout = fallbackInvalidationTimeoutByPr.get(prScopeKey);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }
  const timeout = setTimeout(() => {
    fallbackInvalidationTimeoutByPr.delete(prScopeKey);
    void softInvalidateAfterMutation();
  }, REVIEWER_INVALIDATION_DELAY_MS);
  fallbackInvalidationTimeoutByPr.set(prScopeKey, timeout);
}
export function usePrV2ReviewerActions(owner, repo, number) {
  const $ = _c(125);
  const queryClient = useQueryClient();
  const prScopeKey = `${owner}/${repo}/${number}`;
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2SummaryQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const summaryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DiscussionQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const discussionKey = t1;
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = prV2ReviewsQueryKey(owner, repo, number);
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const reviewsKey = t2;
  let t3;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo) {
    t3 = prV2CommentsQueryKey(owner, repo, number);
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const commentsKey = t3;
  let t4;
  if ($[16] !== number || $[17] !== owner || $[18] !== repo) {
    t4 = mergeabilityStatusQueryKey(owner, repo, number, "v2");
    $[16] = number;
    $[17] = owner;
    $[18] = repo;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const mergeabilityKey = t4;
  let t5;
  if ($[20] !== number || $[21] !== owner || $[22] !== repo) {
    t5 = prV2ReviewerMutationKey(owner, repo, number);
    $[20] = number;
    $[21] = owner;
    $[22] = repo;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const mutationKey = t5;
  let t6;
  if ($[24] !== commentsKey || $[25] !== discussionKey || $[26] !== mergeabilityKey || $[27] !== queryClient || $[28] !== reviewsKey || $[29] !== summaryKey) {
    t6 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey
      }), queryClient.invalidateQueries({
        queryKey: discussionKey
      }), queryClient.invalidateQueries({
        queryKey: reviewsKey
      }), queryClient.invalidateQueries({
        queryKey: commentsKey
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })]);
    };
    $[24] = commentsKey;
    $[25] = discussionKey;
    $[26] = mergeabilityKey;
    $[27] = queryClient;
    $[28] = reviewsKey;
    $[29] = summaryKey;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const invalidateAfterMutation = t6;
  let t7;
  if ($[31] !== commentsKey || $[32] !== discussionKey || $[33] !== mergeabilityKey || $[34] !== queryClient || $[35] !== reviewsKey || $[36] !== summaryKey) {
    t7 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: discussionKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: reviewsKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: commentsKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix(),
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix(),
        refetchType: "none"
      })]);
    };
    $[31] = commentsKey;
    $[32] = discussionKey;
    $[33] = mergeabilityKey;
    $[34] = queryClient;
    $[35] = reviewsKey;
    $[36] = summaryKey;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  const softInvalidateAfterMutation = t7;
  let t8;
  if ($[38] !== number || $[39] !== owner || $[40] !== repo) {
    t8 = async t9 => {
      const {
        toAdd,
        toRemove,
        teamsToAdd,
        teamsToRemove
      } = t9;
      if (toAdd.length > 0 || teamsToAdd.length > 0) {
        await updatePrV2Reviewers(owner, repo, number, {
          method: "POST",
          reviewers: getReviewerLogins(toAdd),
          teamReviewers: teamsToAdd.map(_temp)
        });
      }
      if (toRemove.length > 0 || teamsToRemove.length > 0) {
        await updatePrV2Reviewers(owner, repo, number, {
          method: "DELETE",
          reviewers: getReviewerLogins(toRemove),
          teamReviewers: teamsToRemove.map(_temp2)
        });
      }
    };
    $[38] = number;
    $[39] = owner;
    $[40] = repo;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== number || $[43] !== owner || $[44] !== queryClient || $[45] !== repo || $[46] !== reviewsKey || $[47] !== summaryKey) {
    t9 = async input => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: reviewsKey
      })]);
      const previousReviews = queryClient.getQueryData(reviewsKey);
      const previousSummary = queryClient.getQueryData(summaryKey);
      const previousRequestedReviewers = getRequestedReviewers(previousSummary);
      const previousRequestedLogins = new Set(previousRequestedReviewers.map(_temp3));
      const actualAdds = input.toAdd.filter(reviewer_0 => !previousRequestedLogins.has(normalizeLogin(reviewer_0.login)));
      const actualRemoves = input.toRemove.map(reviewer_1 => {
        const existingReviewer = previousRequestedReviewers.find(requestedReviewer => normalizeLogin(requestedReviewer.login) === normalizeLogin(reviewer_1.login));
        return existingReviewer ? {
          login: existingReviewer.login,
          avatar_url: existingReviewer.avatar_url ?? reviewer_1.avatar_url ?? ""
        } : null;
      }).filter(_temp4);
      const previousRequestedTeams = getRequestedTeamsFromSummary(previousSummary);
      const previousTeamKeys = new Set(previousRequestedTeams.map(getTeamKey));
      const actualTeamAdds = input.teamsToAdd.filter(team_1 => !previousTeamKeys.has(getTeamKey(team_1)));
      const actualTeamRemoves = input.teamsToRemove.filter(team_2 => previousTeamKeys.has(getTeamKey(team_2)));
      queryClient.setQueryData(summaryKey, current => withUpdatedRequestedReviewers(current, previousReviews ?? [], input));
      patchInboxPullRequest(queryClient, {
        owner,
        repo,
        number: Number(number)
      }, pr => {
        const removeLogins = new Set(input.toRemove.map(_temp5));
        const existingLogins = new Set((pr.requested_reviewers ?? []).filter(r_0 => !removeLogins.has(r_0.login.toLowerCase())).map(_temp6));
        const nextReviewers = (pr.requested_reviewers ?? []).filter(r_2 => !removeLogins.has(r_2.login.toLowerCase()));
        for (const reviewer_3 of input.toAdd) {
          if (!existingLogins.has(reviewer_3.login.toLowerCase())) {
            nextReviewers.push({
              login: reviewer_3.login,
              avatar_url: reviewer_3.avatar_url ?? ""
            } as (typeof nextReviewers)[number]);
            existingLogins.add(reviewer_3.login.toLowerCase());
          }
        }
        const removeTeamKeys = new Set(input.teamsToRemove.map(getTeamKey));
        const nextTeams = (pr.requested_teams ?? []).filter(team_3 => !removeTeamKeys.has(getTeamKey(team_3)));
        const existingTeamKeys = new Set(nextTeams.map(getTeamKey));
        for (const team_4 of input.teamsToAdd) {
          if (!existingTeamKeys.has(getTeamKey(team_4))) {
            nextTeams.push({
              name: team_4.name,
              slug: team_4.slug,
              avatar_url: null
            });
            existingTeamKeys.add(getTeamKey(team_4));
          }
        }
        return {
          ...pr,
          requested_reviewers: nextReviewers as typeof pr.requested_reviewers,
          requested_teams: nextTeams,
          updatedAt: new Date().toISOString()
        };
      });
      return {
        inverseInput: {
          toAdd: actualRemoves,
          toRemove: actualAdds,
          teamsToAdd: actualTeamRemoves,
          teamsToRemove: actualTeamAdds
        }
      };
    };
    $[42] = number;
    $[43] = owner;
    $[44] = queryClient;
    $[45] = repo;
    $[46] = reviewsKey;
    $[47] = summaryKey;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== invalidateAfterMutation || $[50] !== number || $[51] !== owner || $[52] !== queryClient || $[53] !== repo || $[54] !== reviewsKey || $[55] !== summaryKey) {
    t10 = (_error, _variables, context) => {
      clearPendingOptimisticMutation({
        owner,
        repo,
        number: Number(number)
      });
      if (!context) {
        return;
      }
      queryClient.setQueryData(summaryKey, current_0 => withUpdatedRequestedReviewers(current_0, queryClient.getQueryData(reviewsKey) ?? [], context.inverseInput));
      invalidateAfterMutation();
    };
    $[49] = invalidateAfterMutation;
    $[50] = number;
    $[51] = owner;
    $[52] = queryClient;
    $[53] = repo;
    $[54] = reviewsKey;
    $[55] = summaryKey;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== prScopeKey || $[58] !== softInvalidateAfterMutation) {
    t11 = () => {
      scheduleReviewerFallbackInvalidation(prScopeKey, softInvalidateAfterMutation);
    };
    $[57] = prScopeKey;
    $[58] = softInvalidateAfterMutation;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== mutationKey || $[61] !== t10 || $[62] !== t11 || $[63] !== t8 || $[64] !== t9) {
    t12 = {
      mutationKey,
      mutationFn: t8,
      onMutate: t9,
      onError: t10,
      onSuccess: t11
    };
    $[60] = mutationKey;
    $[61] = t10;
    $[62] = t11;
    $[63] = t8;
    $[64] = t9;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  const batchUpdateMutation = useMutation(t12);
  let t13;
  if ($[66] !== number || $[67] !== owner || $[68] !== repo) {
    t13 = t14 => {
      const {
        reviewId,
        message
      } = t14;
      return dismissPrV2Review(owner, repo, number, reviewId, message);
    };
    $[66] = number;
    $[67] = owner;
    $[68] = repo;
    $[69] = t13;
  } else {
    t13 = $[69];
  }
  let t14;
  if ($[70] !== number || $[71] !== owner || $[72] !== queryClient || $[73] !== repo || $[74] !== reviewsKey) {
    t14 = async t15 => {
      const {
        reviewId: reviewId_0
      } = t15;
      await queryClient.cancelQueries({
        queryKey: reviewsKey
      });
      const previousReviews_0 = queryClient.getQueryData(reviewsKey);
      const previousReview = previousReviews_0?.find(review => review.githubReviewId === String(reviewId_0));
      queryClient.setQueryData(reviewsKey, current_1 => withDismissedReview(current_1, reviewId_0));
      patchInboxPullRequest(queryClient, {
        owner,
        repo,
        number: Number(number)
      }, pr_0 => {
        const nextReviews = (pr_0.reviews ?? []).map(review_0 => review_0.id === reviewId_0 ? {
          ...review_0,
          state: ReviewerState.DISMISSED
        } : review_0);
        return {
          ...pr_0,
          reviews: nextReviews as typeof pr_0.reviews,
          reviewState: deriveReviewStateFromReviews(nextReviews),
          updatedAt: new Date().toISOString()
        };
      });
      return {
        previousReview
      };
    };
    $[70] = number;
    $[71] = owner;
    $[72] = queryClient;
    $[73] = repo;
    $[74] = reviewsKey;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== invalidateAfterMutation || $[77] !== number || $[78] !== owner || $[79] !== queryClient || $[80] !== repo || $[81] !== reviewsKey) {
    t15 = (_error_0, _variables_0, context_0) => {
      clearPendingOptimisticMutation({
        owner,
        repo,
        number: Number(number)
      });
      if (!context_0) {
        return;
      }
      if (context_0.previousReview) {
        queryClient.setQueryData(reviewsKey, current_2 => restoreReview(current_2, context_0.previousReview));
      }
      invalidateAfterMutation();
    };
    $[76] = invalidateAfterMutation;
    $[77] = number;
    $[78] = owner;
    $[79] = queryClient;
    $[80] = repo;
    $[81] = reviewsKey;
    $[82] = t15;
  } else {
    t15 = $[82];
  }
  let t16;
  if ($[83] !== prScopeKey || $[84] !== softInvalidateAfterMutation) {
    t16 = () => {
      scheduleReviewerFallbackInvalidation(prScopeKey, softInvalidateAfterMutation);
    };
    $[83] = prScopeKey;
    $[84] = softInvalidateAfterMutation;
    $[85] = t16;
  } else {
    t16 = $[85];
  }
  let t17;
  if ($[86] !== mutationKey || $[87] !== t13 || $[88] !== t14 || $[89] !== t15 || $[90] !== t16) {
    t17 = {
      mutationKey,
      mutationFn: t13,
      onMutate: t14,
      onError: t15,
      onSuccess: t16
    };
    $[86] = mutationKey;
    $[87] = t13;
    $[88] = t14;
    $[89] = t15;
    $[90] = t16;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  const dismissReviewMutation = useMutation(t17);
  let t18;
  if ($[92] !== number || $[93] !== owner || $[94] !== repo) {
    t18 = reviewerLogin => updatePrV2Reviewers(owner, repo, number, {
      method: "POST",
      reviewers: [reviewerLogin],
      teamReviewers: []
    });
    $[92] = number;
    $[93] = owner;
    $[94] = repo;
    $[95] = t18;
  } else {
    t18 = $[95];
  }
  let t19;
  if ($[96] !== number || $[97] !== owner || $[98] !== queryClient || $[99] !== repo || $[100] !== reviewsKey || $[101] !== summaryKey) {
    t19 = async reviewerLogin_0 => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: reviewsKey
      })]);
      const previousSummary_0 = queryClient.getQueryData(summaryKey);
      const previousReviews_1 = queryClient.getQueryData(reviewsKey);
      const previousRequestedReviewers_0 = getRequestedReviewers(previousSummary_0);
      const didAddRequestedReviewer = !previousRequestedReviewers_0.some(reviewer_4 => normalizeLogin(reviewer_4.login) === normalizeLogin(reviewerLogin_0));
      const optimisticReviewId = `optimistic-review-request-${normalizeLogin(reviewerLogin_0)}-${Date.now()}`;
      queryClient.setQueryData(summaryKey, current_3 => withUpdatedRequestedReviewers(current_3, previousReviews_1 ?? [], {
        toAdd: [{
          login: reviewerLogin_0,
          avatar_url: ""
        }],
        toRemove: [],
        teamsToAdd: [],
        teamsToRemove: []
      }));
      queryClient.setQueryData(reviewsKey, current_4 => withReRequestedReview(current_4, reviewerLogin_0, optimisticReviewId));
      patchInboxPullRequest(queryClient, {
        owner,
        repo,
        number: Number(number)
      }, pr_1 => {
        const existingLogins_0 = new Set((pr_1.requested_reviewers ?? []).map(_temp7));
        if (existingLogins_0.has(reviewerLogin_0.toLowerCase())) {
          return pr_1;
        }
        return {
          ...pr_1,
          requested_reviewers: [...(pr_1.requested_reviewers ?? []), {
            login: reviewerLogin_0,
            avatar_url: ""
          }] as typeof pr_1.requested_reviewers,
          updatedAt: new Date().toISOString()
        };
      });
      return {
        optimisticReviewId,
        inverseInput: {
          toAdd: [],
          toRemove: didAddRequestedReviewer ? [{
            login: reviewerLogin_0,
            avatar_url: ""
          }] : [],
          teamsToAdd: [],
          teamsToRemove: []
        }
      };
    };
    $[96] = number;
    $[97] = owner;
    $[98] = queryClient;
    $[99] = repo;
    $[100] = reviewsKey;
    $[101] = summaryKey;
    $[102] = t19;
  } else {
    t19 = $[102];
  }
  let t20;
  if ($[103] !== invalidateAfterMutation || $[104] !== number || $[105] !== owner || $[106] !== queryClient || $[107] !== repo || $[108] !== reviewsKey || $[109] !== summaryKey) {
    t20 = (_error_1, _variables_1, context_1) => {
      clearPendingOptimisticMutation({
        owner,
        repo,
        number: Number(number)
      });
      if (!context_1) {
        return;
      }
      queryClient.setQueryData(summaryKey, current_5 => withUpdatedRequestedReviewers(current_5, queryClient.getQueryData(reviewsKey) ?? [], context_1.inverseInput));
      queryClient.setQueryData(reviewsKey, current_6 => removeReviewById(current_6, context_1.optimisticReviewId));
      invalidateAfterMutation();
    };
    $[103] = invalidateAfterMutation;
    $[104] = number;
    $[105] = owner;
    $[106] = queryClient;
    $[107] = repo;
    $[108] = reviewsKey;
    $[109] = summaryKey;
    $[110] = t20;
  } else {
    t20 = $[110];
  }
  let t21;
  if ($[111] !== prScopeKey || $[112] !== softInvalidateAfterMutation) {
    t21 = () => {
      scheduleReviewerFallbackInvalidation(prScopeKey, softInvalidateAfterMutation);
    };
    $[111] = prScopeKey;
    $[112] = softInvalidateAfterMutation;
    $[113] = t21;
  } else {
    t21 = $[113];
  }
  let t22;
  if ($[114] !== mutationKey || $[115] !== t18 || $[116] !== t19 || $[117] !== t20 || $[118] !== t21) {
    t22 = {
      mutationKey,
      mutationFn: t18,
      onMutate: t19,
      onError: t20,
      onSuccess: t21
    };
    $[114] = mutationKey;
    $[115] = t18;
    $[116] = t19;
    $[117] = t20;
    $[118] = t21;
    $[119] = t22;
  } else {
    t22 = $[119];
  }
  const reRequestMutation = useMutation(t22);
  const t23 = batchUpdateMutation.isPending || dismissReviewMutation.isPending || reRequestMutation.isPending;
  let t24;
  if ($[120] !== batchUpdateMutation.mutateAsync || $[121] !== dismissReviewMutation.mutateAsync || $[122] !== reRequestMutation.mutateAsync || $[123] !== t23) {
    t24 = {
      batchUpdateReviewers: batchUpdateMutation.mutateAsync,
      dismissReview: dismissReviewMutation.mutateAsync,
      reRequestReview: reRequestMutation.mutateAsync,
      isMutating: t23
    };
    $[120] = batchUpdateMutation.mutateAsync;
    $[121] = dismissReviewMutation.mutateAsync;
    $[122] = reRequestMutation.mutateAsync;
    $[123] = t23;
    $[124] = t24;
  } else {
    t24 = $[124];
  }
  return t24;
}
function _temp7(r_3) {
  return r_3.login.toLowerCase();
}
function _temp6(r_1) {
  return r_1.login.toLowerCase();
}
function _temp5(r) {
  return r.login.toLowerCase();
}
function _temp4(reviewer_2) {
  return reviewer_2 !== null;
}
function _temp3(reviewer) {
  return normalizeLogin(reviewer.login);
}
function _temp2(team_0) {
  return team_0.slug;
}
function _temp(team) {
  return team.slug;
}